import React from "react";
import { useLocation } from "react-router-dom";
import { Share2 } from "lucide-react";

import { Button } from "../ui/button";
import { PUBLIC_SITE_URL } from "../../constants/app";
import { shareLink } from "../../lib/share";
import { showInfoToast, showSuccessToast } from "../../lib/toast";
import { withLanguageParam } from "../../lib/url-state";

interface ShareLinkButtonProps {
  // Used as the title in the native share sheet (e.g. "Mindset · Quotes").
  title: string;
  className?: string;
  // Icon-only, for the native app's tight toolbar.
  compact?: boolean;
}

// Shares the exact view the user is looking at - path plus query string, so
// the category/type/sort/page filters travel with the link. Uses the native
// share sheet in the app and on touch devices, and copies the full link
// everywhere else.
export function ShareLinkButton({ title, className, compact = false }: ShareLinkButtonProps) {
  const { pathname, search } = useLocation();

  const handleShare = async () => {
    // Always carries ?lang= (English by default) so the recipient sees the
    // quotes in the same language.
    const url = withLanguageParam(`${PUBLIC_SITE_URL}${pathname}${search}`);

    const outcome = await shareLink({ title: `${title} · The Deepam`, url });
    if (outcome === "copied") {
      showSuccessToast("Link to this view copied to clipboard!");
    } else if (outcome === "failed") {
      showInfoToast(`Unable to copy automatically. Link: ${url}`);
    }
  };

  return (
    // Same pill styling as the neighbouring Translate Quotes / filter chips so
    // it sits in the filter row as one of them.
    <Button
      variant="ghost"
      size="sm"
      onClick={handleShare}
      className={`flex items-center gap-1.5 shrink-0 rounded-full whitespace-nowrap ${compact ? "h-10 w-10 justify-center p-0" : "px-3.5"} transition-all duration-200 bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-600 dark:bg-slate-800/80 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-300 ${className ?? ""}`}
      aria-label={`Share a link to ${title}`}
      title="Share a link to this view"
    >
      <Share2 className={compact ? "w-[18px] h-[18px]" : "w-3.5 h-3.5"} />
      <span className={compact ? "sr-only" : "text-xs font-medium"}>Share</span>
    </Button>
  );
}
